import React, { useEffect, useState } from "react";
import axios from "axios";
import { MdDelete } from "react-icons/md";
import { FaTrash, FaPlus } from "react-icons/fa";
import { Icon } from "organisms/iconCommonent";
import { AiOutlinePlusCircle } from "react-icons/ai";

interface Highlight {
  _id: string;
  name: string;
  designation: string;
  desc?: string;
  shortdesc?: string;
  image: string;
  logo: string;
}

export default function Highlights() {
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [showForm, setShowForm] = useState(false);

  // ✅ Fetch all highlights
  const fetchHighlights = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/highLights/alldetails`
      );
      setHighlights(res.data);
    } catch (err) {
      console.error("Error fetching highlights:", err);
    }
  };

  useEffect(() => {
    fetchHighlights();
  }, []);

  // ✅ Delete highlight
  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${process.env.REACT_APP_LOCAL_URL}/api/highLights/delete/${id}`);
      fetchHighlights();
    } catch (err) {
      console.error("Error deleting highlight:", err);
    }
  };

  return (
    <div className="pt-8">
      <div className="flex justify-between items-center ">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-lato-regular">Section - 10</h2>
          <hr />
        </div>

      

        <div className="flex justify-end">
          <button
            className="bg-darkCyan mt-4 flex items-center text-white font-lato-regular rounded-md px-3 py-2"
            onClick={() => setShowForm(true)}
          >
            <span className="text-2xl pr-2">
              <Icon icon={AiOutlinePlusCircle} />
            </span>
            <h6>Add new card</h6>
          </button>
        </div>
      </div>

        <h3 className="font-lato-regular my-4">Buddha Fellow highlights</h3>

      {/* Highlight List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6  bg-white border border-gray-200 rounded-xl overflow-hidden p-4">
        {highlights.map((item) => (
          <div
            key={item._id}
            className="bg-white shadow rounded-lg overflow-hidden border border-gray-200"
          >
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${item.image}`}
              alt={item.name}
              className="w-full h-48 object-cover object-top"
            />
            <div className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <img
                  src={`${process.env.REACT_APP_LOCAL_URL}/${item.logo}`}
                  alt="logo"
                  className="w-10 h-10 rounded-full object-cover border"
                />
                <div>
                  <h3 className="font-bold text-lg">{item.name}</h3>
                  <p className="text-sm text-gray-600">{item.designation}</p>
                </div>
              </div>
              {item.shortdesc && (
                <h6 className="text-black font-lato-regular text-sm mb-2">
                  {item.shortdesc}
                </h6>
              )}
              <h6 className="text-black font-lato-regular text-sm mb-2">
                {item.desc}
              </h6>

              <button
                onClick={() => handleDelete(item._id)}
                className="text-red-500 border border-red-500 px-2 py-1 rounded"
              >
                <Icon icon={MdDelete} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Form Modal */}
      {showForm && (
        <HighlightFormModal
          onClose={() => setShowForm(false)}
          fetchHighlights={fetchHighlights}
        />
      )}
    </div>
  );
}

interface FormProps {
  onClose: () => void;
  fetchHighlights: () => void;
}

const HighlightFormModal = ({ onClose, fetchHighlights }: FormProps) => {
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("");
  const [shortdesc, setShortdesc] = useState("");
  const [desc, setDesc] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !designation.trim() || !imageFile || !logoFile) {
      alert(
        "Please fill all required fields (Name, Designation, Image, Logo)."
      );
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("designation", designation);
    formData.append("shortdesc", shortdesc);
    formData.append("desc", desc);
    formData.append("image", imageFile);
    formData.append("logo", logoFile);

    try {
      await axios.post(
        `${process.env.REACT_APP_LOCAL_URL}/api/highLights/create`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      fetchHighlights();
      onClose();
    } catch (error) {
      console.error("Error creating highlight:", error);
      alert("Failed to create highlight. Check console for details.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-[500px]">
        <h3 className="text-lg font-bold mb-4">Add Highlight</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block font-semibold mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border border-gray-300 rounded-md p-2 w-full"
              required
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Designation *</label>
            <input
              type="text"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              className="border border-gray-300 rounded-md p-2 w-full"
              required
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">
              Short Description
            </label>
            <input
              type="text"
              value={shortdesc}
              onChange={(e) => setShortdesc(e.target.value)}
              className="border border-gray-300 rounded-md p-2 w-full"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Description</label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="border border-gray-300 rounded-md p-2 w-full"
              rows={3}
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Upload Image *</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              className="w-full"
              required
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Upload Logo *</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              className="w-full"
              required
            />
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded-md"
            >
              Create
            </button>
            <button
              type="button"
              onClick={onClose}
              className="border border-gray-400 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
